import { useMemo, useRef, useState } from "react";
import { ColDef, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { PDA1071_R01BomDto } from "./PDA1071_R01Dto";
import usePDA1071_R01Store from "./usePDA1071_R01Store";
import { aggridHeaderClass, pdaCellStyle } from "src/components/common/common";

type BomRow = PDA1071_R01BomDto & { inserted?: boolean; isScanned?: boolean };

const PDA1071_R01DetailTable = () => {
  const { workOrderDetail, selectedItems, setScanValue } = usePDA1071_R01Store();
  const gridRef = useRef<AgGridReact<BomRow>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [, setHoveredRowIndex] = useState<number | null>(null);
  const [, setColumnState] = useState<ColumnState[] | null>(null);

  const bomList = workOrderDetail?.bomList ?? [];

  /** 품목코드별 추가된 이동수량 합산 */
  const qtyByItemCd = useMemo(() => {
    const map = new Map<string, number>();
    selectedItems.forEach((s) => {
      map.set(s.itemCd, (map.get(s.itemCd) ?? 0) + s.moveQty);
    });
    return map;
  }, [selectedItems]);

  /** 이동 목록에 추가된 품목 itemCd 집합 */
  const scannedItemCds = useMemo(
    () => new Set(selectedItems.map((s) => s.itemCd)),
    [selectedItems]
  );

  /** 추가된 행을 맨 위로 정렬 (R01과 동일) */
  const sortedBomList = useMemo<BomRow[]>(() => {
    return [...bomList]
      .map((row) => ({
        ...row,
        isScanned: scannedItemCds.has(row.itemCd),
      }))
      .sort((a, b) => {
        if (a.isScanned && !b.isScanned) return -1;
        if (!a.isScanned && b.isScanned) return 1;
        return 0;
      });
  }, [bomList, scannedItemCds]);

  const columnDefs: ColDef<BomRow>[] = useMemo(
    () => [
      {
        headerName: "품명",
        field: "itemNm",
        valueGetter: (params) => params.data?.itemNm ?? "-",
        cellStyle: (params) => ({
          textAlign: "left",
          ...pdaCellStyle,
          backgroundColor: params.data?.isScanned ? "#fff3cd" : "white",
          fontWeight: params.data?.isScanned ? "bold" : "normal",
        }),
        headerClass: aggridHeaderClass,
        flex: 2,
        minWidth: 100,
      },
      {
        headerName: "위치",
        field: "locationCd",
        valueGetter: (params) => {
          const v = params.data?.locationCd;
          return v && String(v).trim() !== "" ? v : "-";
        },
        cellStyle: (params) => ({
          textAlign: "left",
          ...pdaCellStyle,
          backgroundColor: params.data?.isScanned ? "#fff3cd" : "white",
          fontWeight: params.data?.isScanned ? "bold" : "normal",
        }),
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 90,
      },
      {
        headerName: "수량",
        field: "stockQty",
        valueGetter: (params) => {
          const itemCd = params.data?.itemCd;
          if (!itemCd) return "-";

          const movedQty = qtyByItemCd.get(itemCd);
          if (movedQty !== undefined) return movedQty.toLocaleString();

          const stockQty = params.data?.stockQty;
          return stockQty != null ? Number(stockQty).toLocaleString() : "-";
        },
        cellStyle: (params) => ({
          textAlign: "right",
          ...pdaCellStyle,
          backgroundColor: params.data?.isScanned ? "#fff3cd" : "white",
          fontWeight: params.data?.isScanned ? "bold" : "normal",
        }),
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 90,
      },
    ],
    [qtyByItemCd]
  );

  const handleRowClicked = (bom: PDA1071_R01BomDto) => {
    if (!bom?.itemCd) return;
    setScanValue(bom.itemCd);
  };

  const initSelected = () => {};

  if (!workOrderDetail) {
    return (
      <div className="flex-1 p-3 pt-0 flex w-full h-full flex-col overflow-hidden">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p className="text-lg mb-2">📦</p>
            <p>작업지시를 선택하세요</p>
          </div>
        </div>
      </div>
    );
  }

  if (bomList.length === 0) {
    return (
      <div className="flex-1 p-3 pt-0 flex w-full h-full flex-col overflow-hidden">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p className="text-lg mb-2">📦</p>
            <p>이동할 자재가 없습니다</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-3 pt-0 flex flex-col overflow-hidden min-h-0">
      <GridContextWrapper<BomRow>
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={sortedBomList}
        initSelected={initSelected}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1071_R01_DetailTable_ColumnState"
        enableDataHistory={false}
      >
        <div className="flex-1 w-full h-full min-h-0">
          <Grid_Select<BomRow>
            ref={gridRef}
            rowData={sortedBomList}
            columnDefs={columnDefs}
            rowSelection="single"
            onRowClicked={(event) => {
              if (event.data) handleRowClicked(event.data);
            }}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey="PDA1071_R01_DetailTable_ColumnState"
            setColumnState={setColumnState}
          />
        </div>
      </GridContextWrapper>
    </div>
  );
};

export default PDA1071_R01DetailTable;
